import React, { useState, useEffect, useMemo } from 'react';
import { assembliesApi, fabricationsApi, productsApi, depotsApi, stockApi } from '../lib/api';
import SearchableSelect from '../components/SearchableSelect';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/errorUtils';
import {
  Plus, Layers, Package, X, Hammer, Eye,
  AlertTriangle, FileText, Shield, ChevronRight, History, Trash2,
  ArrowUp, ArrowDown, ArrowUpDown, Check, Warehouse
} from 'lucide-react';

const RISK_CLASS_COLORS = {
  '1.1G': 'bg-red-500/20 text-red-400 border-red-500/30',
  '1.1D': 'bg-red-500/20 text-red-400 border-red-500/30',
  '1.2G': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  '1.2B': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  '1.3G': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  '1.3C': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  '1.4G': 'bg-green-500/20 text-green-400 border-green-500/30',
  '1.4S': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
};

const AssemblagesPage = () => {
  const [assemblies, setAssemblies] = useState([]);
  const [fabrications, setFabrications] = useState([]);
  const [products, setProducts] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assemblies');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showFabricateDialog, setShowFabricateDialog] = useState(false);
  const [selectedAssembly, setSelectedAssembly] = useState(null);
  const [fabricating, setFabricating] = useState(false);
  const [asmSearch, setAsmSearch] = useState('');
  const [asmSortBy, setAsmSortBy] = useState('reference');
  const [asmSortOrder, setAsmSortOrder] = useState('asc');

  const [form, setForm] = useState({
    reference: '',
    designation: '',
    description: '',
    components: [],
    depot_id: '',
  });

  const [fabForm, setFabForm] = useState({
    quantity: 1,
    destination_depot_id: '',
  });

  // fabSources: { [product_id]: [ {depot_id, depot_name, qty} ] }
  const [fabSources, setFabSources] = useState({});
  const [fabStockAvail, setFabStockAvail] = useState(null);

  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assembliesRes, productsRes, depotsRes, fabsRes] = await Promise.all([
        assembliesApi.getAll(),
        productsApi.getAll({ limit: 500 }),
        depotsApi.getAll(),
        fabricationsApi.getAll(),
      ]);
      setAssemblies(assembliesRes.data);
      setProducts((productsRes.data.items || []).filter(p => !p.is_assembly));
      setDepots(depotsRes.data);
      setFabrications(fabsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAssembly = async (assemblyId) => {
    if (!window.confirm('Supprimer cet assemblage ? Cette action est irréversible.')) return;
    try {
      await assembliesApi.delete(assemblyId);
      toast.success('Assemblage supprimé');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  const handleAddComponent = () => {
    if (!selectedProduct || !selectedQuantity || parseInt(selectedQuantity) < 1) {
      toast.error('Sélectionnez un produit et une quantité valide');
      return;
    }
    const product = products.find(p => p.id === selectedProduct);
    if (!product) return;

    if (form.components.some(c => c.product_id === selectedProduct)) {
      toast.error('Ce produit est déjà dans la composition');
      return;
    }

    setForm({
      ...form,
      components: [...form.components, {
        product_id: selectedProduct,
        quantity: parseInt(selectedQuantity),
        reference: product.reference,
        designation: product.designation,
        risk_class: product.risk_class,
        un_code: product.un_code,
        nec: product.nec,
        homologation_number: product.homologation_number,
      }]
    });
    setSelectedProduct('');
    setSelectedQuantity('');
  };

  const handleRemoveComponent = (index) => {
    setForm({
      ...form,
      components: form.components.filter((_, i) => i !== index)
    });
  };

  const computePreview = () => {
    let totalNec = 0;
    const riskClasses = [];
    const unCodes = [];
    const homologations = [];

    form.components.forEach(c => {
      totalNec += (c.nec || 0) * c.quantity;
      if (c.risk_class && !riskClasses.includes(c.risk_class)) riskClasses.push(c.risk_class);
      if (c.un_code && !unCodes.includes(c.un_code)) unCodes.push(c.un_code);
      if (c.homologation_number && !homologations.includes(c.homologation_number)) homologations.push(c.homologation_number);
    });

    const hierarchy = ['1.1G', '1.1D', '1.2G', '1.2B', '1.3G', '1.3C', '1.4G', '1.4S'];
    let mostDangerous = null;
    for (const cls of hierarchy) {
      if (riskClasses.includes(cls)) { mostDangerous = cls; break; }
    }

    return { totalNec, mostDangerous, unCodes, homologations };
  };

  const handleCreateAssembly = async (e) => {
    e.preventDefault();
    if (form.components.length < 2) {
      toast.error('Un assemblage doit contenir au moins 2 composants');
      return;
    }
    try {
      await assembliesApi.create({
        reference: form.reference,
        designation: form.designation,
        description: form.description || null,
        components: form.components.map(c => ({ product_id: c.product_id, quantity: c.quantity })),
        depot_id: form.depot_id || null,
      });
      toast.success('Assemblage créé avec succès');
      setShowCreateDialog(false);
      setForm({ reference: '', designation: '', description: '', components: [], depot_id: '' });
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création'));
    }
  };

  const handleFabricate = async () => {
    if (!selectedAssembly) return;
    if (!fabForm.destination_depot_id) {
      toast.error('Sélectionnez un dépôt de destination');
      return;
    }
    setFabricating(true);
    try {
      // Scale sources by fabrication quantity
      const scaledSources = {};
      for (const [pid, sources] of Object.entries(fabSources)) {
        scaledSources[pid] = sources.map(s => ({ ...s, qty: s.qty * fabForm.quantity }));
      }
      const res = await assembliesApi.fabricate(selectedAssembly.id, {
        quantity: fabForm.quantity,
        destination_depot_id: fabForm.destination_depot_id,
        component_sources: scaledSources,
      });
      toast.success(res.data.message || 'Assemblage fabriqué');
      setShowFabricateDialog(false);
      setFabForm({ quantity: 1, destination_depot_id: '' });
      setFabSources({});
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la fabrication'));
    } finally {
      setFabricating(false);
    }
  };

  const openDetail = (assembly) => {
    setSelectedAssembly(assembly);
    setShowDetailDialog(true);
  };

  const openFabricate = async (assembly) => {
    setSelectedAssembly(assembly);
    setFabForm({ quantity: 1, destination_depot_id: '' });
    setFabSources({});
    setFabStockAvail(null);
    setShowFabricateDialog(true);
    // Load stock availability for components
    if (assembly.components?.length > 0) {
      const pids = assembly.components.map(c => c.product_id).filter(Boolean);
      try {
        const res = await stockApi.availability(pids);
        setFabStockAvail(res.data);
        // Auto-assign from depots with most stock
        const auto = {};
        for (const comp of assembly.components) {
          const avail = res.data?.[comp.product_id];
          if (avail?.depots?.length > 0) {
            const sorted = [...avail.depots].sort((a, b) => b.available - a.available);
            const sources = [];
            let remaining = comp.quantity;
            for (const d of sorted) {
              if (remaining <= 0 || d.available <= 0) break;
              const take = Math.min(remaining, d.available);
              sources.push({ depot_id: d.depot_id, depot_name: d.depot_name, qty: take });
              remaining -= take;
            }
            if (sources.length > 0) auto[comp.product_id] = sources;
          }
        }
        setFabSources(auto);
      } catch { /* silent */ }
    }
  };

  const handleFabSourceQty = (productId, depotId, depotName, newQty) => {
    setFabSources(prev => {
      const current = prev[productId] || [];
      let updated;
      if (newQty <= 0) {
        updated = current.filter(s => s.depot_id !== depotId);
      } else {
        const existing = current.find(s => s.depot_id === depotId);
        if (existing) {
          updated = current.map(s => s.depot_id === depotId ? { ...s, qty: newQty } : s);
        } else {
          updated = [...current, { depot_id: depotId, depot_name: depotName, qty: newQty }];
        }
      }
      return { ...prev, [productId]: updated };
    });
  };

  const getFabAllocated = (productId) => {
    return (fabSources[productId] || []).reduce((s, a) => s + (a.qty || 0), 0);
  };

  const preview = computePreview();

  return (
    <div className="space-y-6" data-testid="assemblages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Assemblages</h1>
          <p className="text-zinc-400 text-sm mt-1">Compositions de produits (grappes, assemblages)</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="create-assembly-button">
              <Plus className="w-4 h-4 mr-2" />
              Nouvel Assemblage
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-heading flex items-center gap-2">
                <Layers className="w-5 h-5 text-amber-400" />
                Nouvelle Composition
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAssembly} className="space-y-6">
              {/* Basic info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Référence *</Label>
                  <Input
                    value={form.reference}
                    onChange={(e) => setForm({ ...form, reference: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-white font-mono"
                    placeholder="GRP-001"
                    required
                    data-testid="assembly-reference-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Désignation *</Label>
                  <Input
                    value={form.designation}
                    onChange={(e) => setForm({ ...form, designation: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    placeholder="Grappe 3 bombes 100mm"
                    required
                    data-testid="assembly-designation-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Dépôt par défaut</Label>
                  <Select value={form.depot_id} onValueChange={(v) => setForm({ ...form, depot_id: v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white">
                      <SelectValue placeholder="Optionnel" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {depots.map(d => (
                        <SelectItem key={d.id} value={d.id} className="text-white">{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Description</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 text-white"
                  placeholder="Description de l'assemblage..."
                />
              </div>

              {/* Components */}
              <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                <Label className="text-zinc-300 mb-3 block">Composants de l'assemblage</Label>
                <div className="flex gap-2 mb-4">
                  <div className="flex-1">
                    <SearchableSelect
                      value={selectedProduct}
                      onValueChange={setSelectedProduct}
                      options={products.map(p => ({ value: p.id, label: `${p.reference} — ${p.designation || ''}`, searchText: `${p.reference} ${p.designation}` }))}
                      placeholder="Rechercher un produit..."
                      searchPlaceholder="Ref ou designation..."
                      data-testid="component-product-select"
                    />
                  </div>
                  <Input
                    type="number"
                    value={selectedQuantity}
                    onChange={(e) => setSelectedQuantity(e.target.value)}
                    className="w-24 bg-zinc-900 border-zinc-700 text-white"
                    placeholder="Qté"
                    min="1"
                    data-testid="component-quantity-input"
                  />
                  <Button type="button" onClick={handleAddComponent} variant="outline" className="border-zinc-700" data-testid="add-component-button">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {form.components.length > 0 && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-7 gap-2 text-xs text-zinc-500 px-2">
                      <span>Référence</span>
                      <span className="col-span-2">Désignation</span>
                      <span>Classe ADR</span>
                      <span className="text-right">Qté</span>
                      <span className="text-right">NEC (g)</span>
                      <span className="text-right">Homol.</span>
                    </div>
                    {form.components.map((comp, index) => (
                      <div key={index} className="grid grid-cols-7 gap-2 items-center p-2 bg-zinc-900 rounded border border-zinc-800" data-testid={`component-row-${index}`}>
                        <span className="font-mono text-cyan-400 text-sm">{comp.reference}</span>
                        <span className="text-white text-sm col-span-2 truncate">{comp.designation}</span>
                        <div>
                          {comp.risk_class && (
                            <Badge variant="outline" className={`text-xs ${RISK_CLASS_COLORS[comp.risk_class] || ''}`}>
                              {comp.risk_class}
                            </Badge>
                          )}
                        </div>
                        <span className="font-mono text-white text-sm text-right">{comp.quantity}</span>
                        <span className="font-mono text-amber-400 text-sm text-right">
                          {((comp.nec || 0) * comp.quantity).toFixed(3)}
                        </span>
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-xs text-zinc-500 truncate max-w-[60px]">{comp.homologation_number || '-'}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveComponent(index)}
                            className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Computed preview */}
              {form.components.length >= 2 && (
                <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                  <h4 className="text-sm font-semibold text-amber-400 mb-3">Résumé calculé de l'assemblage</h4>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-zinc-500">NEC totale</p>
                      <p className="text-lg font-mono text-white">{preview.totalNec.toFixed(3)} kg</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500">Classe ADR (la + dangereuse)</p>
                      <Badge variant="outline" className={`mt-1 ${RISK_CLASS_COLORS[preview.mostDangerous] || ''}`}>
                        {preview.mostDangerous || '-'}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500">Codes ONU</p>
                      <p className="font-mono text-red-400 text-sm">{preview.unCodes.join(', ') || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500">N° Homologation</p>
                      <p className="text-xs text-zinc-300">{preview.homologations.join(', ') || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="border-zinc-700">
                  Annuler
                </Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black" disabled={form.components.length < 2} data-testid="submit-assembly-button">
                  <Layers className="w-4 h-4 mr-2" /> Créer l'assemblage
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="assemblies" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Layers className="w-4 h-4 mr-2" /> Compositions ({assemblies.length})
          </TabsTrigger>
          <TabsTrigger value="fabrications" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <History className="w-4 h-4 mr-2" /> Fabrications ({fabrications.length})
          </TabsTrigger>
        </TabsList>

        {/* Assemblies Tab */}
        <TabsContent value="assemblies" className="mt-6">
          {/* Search & Sort Toolbar */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <input value={asmSearch} onChange={e => setAsmSearch(e.target.value)}
                placeholder="Rechercher par reference, designation..."
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-4 pr-4 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none"
                data-testid="assembly-search" />
            </div>
            <span className="text-zinc-600 text-xs">Tri:</span>
            <select value={asmSortBy} onChange={e => setAsmSortBy(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-zinc-300">
              <option value="reference">Reference</option>
              <option value="designation">Designation</option>
              <option value="risk_class">ADR</option>
            </select>
            <button onClick={() => setAsmSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
              className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-zinc-300 hover:border-amber-500/50">
              {asmSortOrder === 'asc' ? '↑ A-Z' : '↓ Z-A'}
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (() => {
            let filtered = assemblies;
            if (asmSearch) {
              const q = asmSearch.toLowerCase();
              filtered = filtered.filter(a =>
                a.reference?.toLowerCase().includes(q) ||
                a.designation?.toLowerCase().includes(q) ||
                (a.risk_class || '').toLowerCase().includes(q)
              );
            }
            filtered = [...filtered].sort((a, b) => {
              let va = a[asmSortBy] || '', vb = b[asmSortBy] || '';
              if (va < vb) return asmSortOrder === 'asc' ? -1 : 1;
              if (va > vb) return asmSortOrder === 'asc' ? 1 : -1;
              return 0;
            });
            if (filtered.length === 0) return (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-12 text-center">
                  <Layers className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                  <h3 className="text-xl text-white mb-2">Aucun assemblage{asmSearch ? ' trouve' : ' defini'}</h3>
                </CardContent>
              </Card>
            );
            return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filtered.map((asm) => (
                <Card key={asm.id} className="bg-zinc-900/50 border-zinc-800 card-hover" data-testid={`assembly-card-${asm.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                          <Layers className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <CardTitle className="text-white text-lg">{asm.designation}</CardTitle>
                          <p className="font-mono text-cyan-400 text-sm">{asm.reference}</p>
                        </div>
                      </div>
                      {asm.risk_class && (
                        <Badge variant="outline" className={RISK_CLASS_COLORS[asm.risk_class] || ''}>
                          {asm.risk_class}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Component summary */}
                    <div className="space-y-2">
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Composants ({asm.components?.length || 0})</p>
                      {asm.components?.slice(0, 6).map((comp, idx) => (
                        <div key={idx} className="flex items-center justify-between px-3 py-2 bg-zinc-950 rounded border border-zinc-800">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Package className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                            <span className="font-mono text-cyan-400 text-sm flex-shrink-0">{comp.reference}</span>
                            <span className="text-zinc-400 text-sm truncate">{comp.designation}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            {comp.homologation_number && (
                              <span className="font-mono text-green-400/70 text-[10px] bg-green-500/10 px-1.5 py-0.5 rounded">{comp.homologation_number}</span>
                            )}
                            <span className="font-mono text-white text-sm">x{comp.quantity}</span>
                            {comp.risk_class && (
                              <Badge variant="outline" className={`text-xs ${RISK_CLASS_COLORS[comp.risk_class] || ''}`}>
                                {comp.risk_class}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                      {(asm.components?.length || 0) > 6 && (
                        <p className="text-xs text-zinc-500 text-center">+ {asm.components.length - 6} autres composants</p>
                      )}
                    </div>

                    {/* Computed data */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800 text-center">
                        <p className="text-xs text-zinc-500">NEC</p>
                        <p className="font-mono text-amber-400">{((asm.total_nec || 0) * 1000).toFixed(1)} g</p>
                      </div>
                      <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800 text-center">
                        <p className="text-xs text-zinc-500">Fabriqués</p>
                        <p className="font-mono text-white">{asm.fabrication_count || 0}</p>
                      </div>
                      <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800 text-center">
                        <p className="text-xs text-zinc-500">Homologations</p>
                        <p className="font-mono text-green-400 text-sm">{asm.homologation_numbers?.length || 0}</p>
                      </div>
                    </div>

                    {/* Homologation numbers */}
                    {asm.homologation_numbers?.length > 0 && (
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                        <p className="text-xs text-green-400 mb-1">N° Homologation</p>
                        <p className="text-sm text-zinc-300 font-mono">{asm.homologation_numbers.join(' / ')}</p>
                      </div>
                    )}

                    {/* UN Codes */}
                    {asm.un_codes?.length > 0 && (
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-zinc-500">ONU:</p>
                        {asm.un_codes.map((code, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-red-500/10 text-red-400 border-red-500/20">
                            {code}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t border-zinc-800">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-zinc-700"
                        onClick={() => openDetail(asm)}
                        data-testid={`view-assembly-${asm.id}`}
                      >
                        <Eye className="w-4 h-4 mr-2" /> Détails
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        onClick={() => openFabricate(asm)}
                        data-testid={`fabricate-assembly-${asm.id}`}
                      >
                        <Hammer className="w-4 h-4 mr-2" /> Fabriquer
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                        onClick={() => handleDeleteAssembly(asm.id)}
                        data-testid={`delete-assembly-${asm.id}`}
                        title="Supprimer l'assemblage"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            );
          })()}
        </TabsContent>

        {/* Fabrications Tab */}
        <TabsContent value="fabrications" className="mt-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-350px)]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400">ID Fabrication</TableHead>
                      <TableHead className="text-zinc-400">Assemblage</TableHead>
                      <TableHead className="text-zinc-400">Quantité</TableHead>
                      <TableHead className="text-zinc-400">Date</TableHead>
                      <TableHead className="text-zinc-400">Opérateur</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fabrications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-zinc-500">
                          <Hammer className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>Aucune fabrication enregistrée</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      fabrications.map((fab) => (
                        <TableRow key={fab.id} className="border-zinc-800 table-row-hover">
                          <TableCell className="font-mono text-cyan-400">{fab.id}</TableCell>
                          <TableCell>
                            <span className="font-mono text-amber-400">{fab.assembly_reference}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                              x{fab.quantity}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-zinc-400">
                            {new Date(fab.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </TableCell>
                          <TableCell className="text-white">{fab.user_name}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Layers className="w-5 h-5 text-amber-400" />
              {selectedAssembly?.designation}
            </DialogTitle>
          </DialogHeader>
          {selectedAssembly && (
            <div className="space-y-6">
              {/* Header info */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">Référence</p>
                  <p className="font-mono text-cyan-400">{selectedAssembly.reference}</p>
                </div>
                <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">NEC Totale</p>
                  <p className="font-mono text-amber-400 text-lg">{((selectedAssembly.total_nec || 0) * 1000).toFixed(1)} g</p>
                </div>
                <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">Classe ADR</p>
                  {selectedAssembly.risk_class ? (
                    <Badge variant="outline" className={`mt-1 ${RISK_CLASS_COLORS[selectedAssembly.risk_class] || ''}`}>
                      {selectedAssembly.risk_class}
                    </Badge>
                  ) : <p className="text-zinc-500">-</p>}
                </div>
                <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">Poids brut</p>
                  <p className="font-mono text-white">{((selectedAssembly.total_gross_weight || 0) * 1000).toFixed(1)} g</p>
                </div>
              </div>

              {/* Components table */}
              <div>
                <h4 className="text-sm font-semibold text-amber-400 mb-3">Composants ({selectedAssembly.components?.length})</h4>
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Référence</TableHead>
                      <TableHead className="text-zinc-400">Désignation</TableHead>
                      <TableHead className="text-zinc-400">Classe</TableHead>
                      <TableHead className="text-zinc-400">ONU</TableHead>
                      <TableHead className="text-zinc-400 text-right">Qté</TableHead>
                      <TableHead className="text-zinc-400 text-right">NEC unit.</TableHead>
                      <TableHead className="text-zinc-400 text-right">NEC total</TableHead>
                      <TableHead className="text-zinc-400">Homologation</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedAssembly.components?.map((comp, idx) => (
                      <TableRow key={idx} className="border-zinc-800">
                        <TableCell className="font-mono text-cyan-400">{comp.reference}</TableCell>
                        <TableCell className="text-white">{comp.designation?.substring(0, 25)}</TableCell>
                        <TableCell>
                          {comp.risk_class && (
                            <Badge variant="outline" className={`text-xs ${RISK_CLASS_COLORS[comp.risk_class] || ''}`}>
                              {comp.risk_class}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-red-400">{comp.un_code || '-'}</TableCell>
                        <TableCell className="text-right font-mono text-white">{comp.quantity}</TableCell>
                        <TableCell className="text-right font-mono text-zinc-400">{(comp.nec_unit || 0).toFixed(3)}</TableCell>
                        <TableCell className="text-right font-mono text-amber-400">{(comp.nec_total || 0).toFixed(3)}</TableCell>
                        <TableCell className="font-mono text-xs text-green-400">{comp.homologation_number || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Homologation & UN codes */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-green-400" />
                    <p className="text-sm font-semibold text-green-400">Homologations combinées</p>
                  </div>
                  {selectedAssembly.homologation_numbers?.length > 0 ? (
                    <div className="space-y-1">
                      {selectedAssembly.homologation_numbers.map((num, idx) => (
                        <p key={idx} className="font-mono text-sm text-white">{num}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-sm">Aucun numéro d'homologation</p>
                  )}
                </div>
                <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <p className="text-sm font-semibold text-red-400">Codes ONU</p>
                  </div>
                  {selectedAssembly.un_codes?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedAssembly.un_codes.map((code, idx) => (
                        <Badge key={idx} variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30 font-mono">
                          {code}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-sm">Aucun code ONU</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fabrication Dialog */}
      <Dialog open={showFabricateDialog} onOpenChange={setShowFabricateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Hammer className="w-5 h-5 text-green-400" />
              Fabriquer un assemblage
            </DialogTitle>
          </DialogHeader>
          {selectedAssembly && (
            <div className="space-y-5">
              {/* Assembly summary */}
              <div className="p-4 bg-zinc-950 rounded-xl border border-zinc-800">
                <p className="font-mono text-cyan-400">{selectedAssembly.reference}</p>
                <p className="text-white">{selectedAssembly.designation}</p>
                <div className="flex items-center gap-3 mt-2">
                  <Badge variant="outline" className={RISK_CLASS_COLORS[selectedAssembly.risk_class] || ''}>
                    {selectedAssembly.risk_class}
                  </Badge>
                  <span className="text-amber-400 font-mono text-sm">{((selectedAssembly.total_nec || 0) * 1000).toFixed(1)} g NEC</span>
                  {selectedAssembly.homologation_numbers?.length > 0 && (
                    <span className="text-cyan-400/70 text-xs">{selectedAssembly.homologation_numbers.length} homologation(s)</span>
                  )}
                </div>
              </div>

              {/* Quantity + Destination depot */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Quantité à fabriquer</Label>
                  <Input
                    type="number"
                    value={fabForm.quantity}
                    onChange={(e) => setFabForm({ ...fabForm, quantity: parseInt(e.target.value) || 1 })}
                    className="h-14 text-2xl text-center font-mono bg-zinc-950 border-zinc-700 text-white"
                    min="1"
                    data-testid="fabrication-quantity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300 flex items-center gap-1.5">
                    <Warehouse className="w-3.5 h-3.5" /> Dépôt de destination *
                  </Label>
                  <Select value={fabForm.destination_depot_id} onValueChange={(v) => setFabForm({ ...fabForm, destination_depot_id: v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-14 text-sm" data-testid="fabrication-destination-depot">
                      <SelectValue placeholder="Où stocker l'assemblage fini" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {depots.map(d => (
                        <SelectItem key={d.id} value={d.id} className="text-white">{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Components with multi-depot sourcing */}
              <div className="rounded-xl border border-zinc-800 overflow-hidden">
                <div className="px-4 py-3 bg-zinc-900/80 border-b border-zinc-800">
                  <p className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">
                    Composants nécessaires (x{fabForm.quantity})
                  </p>
                  <p className="text-zinc-500 text-[11px] mt-0.5">Indiquez dans quel(s) dépôt(s) prélever chaque composant</p>
                </div>

                <div className="max-h-[35vh] overflow-y-auto">
                  {selectedAssembly.components?.map((comp, idx) => {
                    const needed = comp.quantity * (fabForm.quantity || 1);
                    const allocated = getFabAllocated(comp.product_id) * (fabForm.quantity || 1);
                    const remaining = needed - allocated;
                    const avail = fabStockAvail?.[comp.product_id];

                    return (
                      <div key={idx} className={`px-4 py-3 border-b border-zinc-800/50 ${remaining > 0 ? 'bg-red-500/5' : ''}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-cyan-400 text-xs">{comp.reference}</span>
                          <span className="text-zinc-300 text-sm truncate flex-1">{comp.designation}</span>
                          <span className="text-white font-mono text-sm font-semibold">{needed}</span>
                          {comp.homologation_number && (
                            <Badge variant="outline" className="text-[9px] text-cyan-400/60 border-cyan-500/20">{comp.homologation_number}</Badge>
                          )}
                          {remaining === 0 && <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px]">OK</Badge>}
                          {remaining > 0 && <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px]">Reste {remaining}</Badge>}
                        </div>
                        <div className="flex flex-wrap gap-2 pl-2">
                          {avail ? (
                            avail.depots.length > 0 ? avail.depots.map(d => {
                              const allocs = fabSources[comp.product_id] || [];
                              const alloc = allocs.find(a => a.depot_id === d.depot_id);
                              const qty = alloc?.qty || 0;
                              const isActive = qty > 0;
                              return (
                                <div key={d.depot_id} className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border transition-colors ${
                                  isActive ? 'bg-amber-500/10 border-amber-500/30' : 'border-zinc-800 hover:border-zinc-600'
                                }`}>
                                  <span className={`text-xs truncate max-w-[80px] ${isActive ? 'text-amber-300' : 'text-zinc-500'}`}>{d.depot_name}</span>
                                  <span className={`font-mono text-[10px] ${d.available > 0 ? 'text-zinc-500' : 'text-red-400'}`}>({d.available})</span>
                                  <input type="number" min="0" max={d.available}
                                    value={qty || ''}
                                    placeholder="0"
                                    onChange={(e) => handleFabSourceQty(comp.product_id, d.depot_id, d.depot_name, parseInt(e.target.value) || 0)}
                                    className={`w-12 h-5 rounded border px-1 text-xs text-center outline-none bg-zinc-950 ${
                                      isActive ? 'border-amber-500/40 text-amber-300' : 'border-zinc-700 text-zinc-400'
                                    } focus:border-amber-500/50`}
                                  />
                                </div>
                              );
                            }) : (
                              <span className="text-red-400 text-xs">Aucun stock disponible</span>
                            )
                          ) : (
                            <span className="text-zinc-600 text-xs">Chargement...</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Info: homologation & NEC are preserved */}
              <div className="p-3 bg-cyan-500/5 rounded-lg border border-cyan-500/20 flex items-start gap-2">
                <Shield className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-cyan-400/80 text-xs leading-relaxed">
                  Toutes les homologations et NEC de chaque composant sont conservées dans l'assemblage pour la traçabilité et l'export des documents de transport.
                </p>
              </div>

              <Button
                onClick={handleFabricate}
                className="w-full h-12 bg-green-500 hover:bg-green-400 text-black font-semibold"
                disabled={fabricating || !fabForm.destination_depot_id}
                data-testid="confirm-fabrication-button"
              >
                {fabricating ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Hammer className="w-5 h-5 mr-2" /> Confirmer la fabrication
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssemblagesPage;
